import {
  S3Client,
  ListObjectsV2Command,
  ListObjectsCommand,
  PutObjectCommand,
  HeadObjectCommand,
  CopyObjectCommand,
  DeleteObjectsCommand,
  GetObjectCommand,
} from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { fromCognitoIdentity } from "@aws-sdk/credential-provider-cognito-identity";
import { api } from "services/restUtil";
import { PreviewType } from "../../components/content/ContentActions/components/FolderMetadataDialog/FolderMetadataDialog";
import {
  ASSETS_FOLDER_NAME,
  File as ContentFile,
  EDITOR_FILE_NAME,
  Folder,
  LibraryObject,
} from "services/content/model";
import { getAWSConfig } from "./../config";

export default class S3LibraryStore {
  private bucket: string = "";
  private s3: S3Client | null = null;
  private checkCredentialsPromise: Promise<void> | null = null;

  private requireS3() {
    if (!this.s3) throw new Error("Invalid configuration of s3 service.");
    return this.s3;
  }

  private async refreshToken() {
    const resp = await getAWSConfig();
    const credentialsProvider = fromCognitoIdentity({
      clientConfig: { region: resp.region },
      identityId: resp.cognito.aws_cognito_identity_id,
      logins: {
        // Fetches a fresh Cognito token on every AWS credential refresh so
        // long-lived sessions don't fail after the initial token expires.
        "cognito-identity.amazonaws.com": () =>
          getAWSConfig().then(r => r.cognito.aws_cognito_token),
      },
    });
    this.bucket = resp.bucket;
    this.s3 = new S3Client({
      region: resp.region,
      credentials: credentialsProvider,
    });
  }

  private async doCheckCredentials() {
    if (!this.s3) {
      await this.refreshToken();
    }
  }

  private checkCredentials() {
    if (this.checkCredentialsPromise) return this.checkCredentialsPromise;
    const promise = this.doCheckCredentials().finally(() => {
      this.checkCredentialsPromise = null;
    });
    this.checkCredentialsPromise = promise;
    return promise;
  }

  async listFolderContents(key: string, maxNumberOfKeys?: number) {
    await this.checkCredentials();
    const s3 = this.requireS3();

    const params = {
      Bucket: this.bucket,
      Delimiter: "/",
      ...(key !== "" && { Prefix: key }),
      ...(maxNumberOfKeys && { MaxKeys: maxNumberOfKeys }),
    };

    const data = await s3.send(new ListObjectsV2Command(params));
    const folders: Folder[] = (data.CommonPrefixes ?? []).flatMap(cp => {
      const prefix = cp.Prefix ?? "";
      const name = prefix.split("/").at(-2) ?? "";
      if (name.match(/^[\da-z]{32}$/i) || name === ASSETS_FOLDER_NAME) return [];
      return [{ prefix, key: prefix, name, isPrivate: false }];
    });

    const files: ContentFile[] = (data.Contents ?? []).flatMap(item => {
      if (!item.Key || item.Key.endsWith("/")) return [];
      const name = item.Key.split("/").at(-1) ?? "";
      if (name === EDITOR_FILE_NAME) return [];
      return [
        {
          key: item.Key,
          prefix: key,
          name,
          isPrivate: false,
          lastModified: item.LastModified,
        },
      ];
    });

    return { folders, files };
  }

  private cleanMetadata(metadata: string | null | undefined) {
    return metadata && metadata.trim() && metadata.replaceAll("\n", "");
  }

  private buildFolderMetadata(object: LibraryObject) {
    const metadata: Record<string, string> = {};
    const author = this.cleanMetadata(object.author);
    if (author) metadata.author = author;
    const description = this.cleanMetadata(object.description);
    if (description) metadata.description = description;

    let previewName = "";
    let previewVerticalName = "";
    let previewHorizontalName = "";

    if (object.preview) {
      previewName = `${object.name}-preview.${object.preview.name.split(".")[1]}`;
      metadata.preview = previewName;
    } else if ("preview_url" in object && object.preview_url) {
      metadata.preview = extractFilename(object.preview_url);
    }
    if (object.preview_horizontal) {
      previewHorizontalName = `${object.name}-horizontal-preview.${object.preview_horizontal.name.split(".")[1]}`;
      metadata.preview_horizontal = previewHorizontalName;
    } else if ("preview_horizontal_url" in object && object.preview_horizontal_url) {
      metadata.preview_horizontal = extractFilename(object.preview_horizontal_url);
    }
    if (object.preview_vertical) {
      previewVerticalName = `${object.name}vertical-preview.${object.preview_vertical.name.split(".")[1]}`;
      metadata.preview_vertical = previewVerticalName;
    } else if ("preview_vertical_url" in object && object.preview_vertical_url) {
      metadata.preview_vertical = extractFilename(object.preview_vertical_url);
    }

    if (object.order) metadata.order = String(object.order);
    if (object.length) metadata.length = String(object.length);
    if (object.readings) metadata.readings = String(object.readings);
    if (object.videos) metadata.videos = String(object.videos);
    if (object.subtype) metadata.subtype = object.subtype;

    return { metadata, previewName, previewVerticalName, previewHorizontalName };
  }

  private async uploadPreviews(
    s3: S3Client,
    object: LibraryObject,
    previewType: PreviewType,
    previewName: string,
    previewVerticalName: string,
    previewHorizontalName: string,
    folderKey: string
  ) {
    const secondPreviewType =
      previewType === PreviewType.VERTICAL
        ? PreviewType.HORIZONTAL
        : PreviewType.VERTICAL;
    const mainPreviewName =
      previewType === PreviewType.VERTICAL ? previewVerticalName : previewHorizontalName;
    const secondPreviewName =
      previewType === PreviewType.VERTICAL ? previewHorizontalName : previewVerticalName;

    const uploads: Promise<unknown>[] = [];
    if (object.preview) {
      uploads.push(
        s3.send(
          new PutObjectCommand({
            Bucket: this.bucket,
            Key: `${folderKey}${ASSETS_FOLDER_NAME}/${previewName}`,
            Body: object.preview as Blob,
          })
        )
      );
    }
    if (object[previewType]) {
      uploads.push(
        s3.send(
          new PutObjectCommand({
            Bucket: this.bucket,
            Key: `${folderKey}${ASSETS_FOLDER_NAME}/${mainPreviewName}`,
            Body: object[previewType] as Blob,
          })
        )
      );
    }
    if (object[secondPreviewType]) {
      uploads.push(
        s3.send(
          new PutObjectCommand({
            Bucket: this.bucket,
            Key: `${folderKey}${ASSETS_FOLDER_NAME}/${secondPreviewName}`,
            Body: object[secondPreviewType] as Blob,
          })
        )
      );
    }
    await Promise.all(uploads);
  }

  private async uploadFiles(
    s3: S3Client,
    folderKey: string,
    contentFiles: globalThis.File[],
    assetFiles: globalThis.File[]
  ) {
    await Promise.all(
      contentFiles.map(file =>
        s3.send(
          new PutObjectCommand({
            Bucket: this.bucket,
            Key: `${folderKey}${file.name}`,
            Body: file,
            ContentType: file.type,
          })
        )
      )
    );
    if (assetFiles.length > 0) {
      await Promise.all(
        assetFiles.map(file =>
          s3.send(
            new PutObjectCommand({
              Bucket: this.bucket,
              Key: `${folderKey}${ASSETS_FOLDER_NAME}/${file.name}`,
              Body: file,
            })
          )
        )
      );
    }
  }

  async createFolder(object: LibraryObject, parentKey: string) {
    await this.checkCredentials();
    const s3 = this.requireS3();
    const { metadata, previewName, previewVerticalName, previewHorizontalName } =
      this.buildFolderMetadata(object);
    const folderKey = `${parentKey}${object.name}/`;
    await s3.send(
      new PutObjectCommand({ Bucket: this.bucket, Key: folderKey, Metadata: metadata })
    );
    await this.uploadPreviews(
      s3,
      object,
      PreviewType.VERTICAL,
      previewName,
      previewVerticalName,
      previewHorizontalName,
      folderKey
    );
  }

  async createFile(
    object: LibraryObject,
    parentKey: string,
    contentFiles: globalThis.File[],
    assetFiles: globalThis.File[]
  ) {
    await this.createFolder(object, parentKey);
    const s3 = this.requireS3();
    await this.uploadFiles(s3, `${parentKey}${object.name}/`, contentFiles, assetFiles);
  }

  async updateFile(
    parentKey: string,
    contentFiles: globalThis.File[],
    assetFiles: globalThis.File[]
  ) {
    await this.checkCredentials();
    const s3 = this.requireS3();
    await this.uploadFiles(s3, `${parentKey}/`, contentFiles, assetFiles);
  }

  async fetchObjectMetadata(key: string): Promise<{ libraryObject: LibraryObject }> {
    const data = await this.headObject(key);
    if (!data.Metadata) throw new Error("No metadata found.");

    const libraryObject = new LibraryObject();
    const normalizedKey = key.endsWith("/") ? key.slice(0, -1) : key;
    libraryObject.key = key;
    libraryObject.name = normalizedKey.split("/").at(-1) ?? "";

    const meta = data.Metadata;
    if (meta.author) libraryObject.author = decodeURIComponent(meta.author);
    if (meta.description)
      libraryObject.description = decodeURIComponent(meta.description);
    if (meta.order) libraryObject.order = parseInt(meta.order, 10);
    if (meta.length) libraryObject.length = parseInt(meta.length, 10);
    if (meta.subtype) libraryObject.subtype = decodeURIComponent(meta.subtype);
    if (meta.readings) libraryObject.readings = parseInt(meta.readings, 10);
    if (meta.videos) libraryObject.videos = parseInt(meta.videos, 10);

    const signedUrl = (metaKey: string) =>
      getSignedUrl(
        this.s3!,
        new GetObjectCommand({
          Bucket: this.bucket,
          Key: `${key}${ASSETS_FOLDER_NAME}/${decodeURIComponent(metaKey)}`,
        }),
        { expiresIn: 3600 }
      );
    if (meta.preview) libraryObject.preview_url = await signedUrl(meta.preview);
    if (meta.preview_vertical)
      libraryObject.preview_vertical_url = await signedUrl(meta.preview_vertical);
    if (meta.preview_horizontal)
      libraryObject.preview_horizontal_url = await signedUrl(meta.preview_horizontal);

    return { libraryObject };
  }

  private async headObject(key: string) {
    await this.checkCredentials();
    const s3 = this.requireS3();
    if (!key.trim()) throw new Error("No key provided.");
    return s3.send(new HeadObjectCommand({ Bucket: this.bucket, Key: key }));
  }

  async updateFolderMetadata(object: LibraryObject) {
    await this.checkCredentials();
    const s3 = this.requireS3();
    const { metadata, previewName, previewVerticalName, previewHorizontalName } =
      this.buildFolderMetadata(object);
    const folderKey = object.key;
    await s3.send(
      new CopyObjectCommand({
        Bucket: this.bucket,
        CopySource: `${this.bucket}/${folderKey}`,
        Key: folderKey,
        Metadata: metadata,
        MetadataDirective: "REPLACE",
      })
    );
    await this.uploadPreviews(
      s3,
      object,
      PreviewType.HORIZONTAL,
      previewName,
      previewVerticalName,
      previewHorizontalName,
      folderKey
    );
  }

  async renameFolder(object: LibraryObject) {
    let folderKey = object.key.endsWith("/") ? object.key.slice(0, -1) : object.key;
    const parts = folderKey.split("/");
    if (parts.at(-1) === object.name) return;
    parts[parts.length - 1] = object.name;
    await this.moveFolder(object.key, parts.join("/") + "/");
  }

  async removeFolder(key: string) {
    await this.checkCredentials();
    const s3 = this.requireS3();
    if (!key.trim()) throw new Error("No key provided.");

    const listed = await s3.send(
      new ListObjectsCommand({ Bucket: this.bucket, Prefix: key })
    );
    if (!listed.Contents?.length) throw new Error("No data to delete");

    const objects = listed.Contents.map(obj => ({
      Key: obj.Key,
    }));
    await s3.send(
      new DeleteObjectsCommand({
        Bucket: this.bucket,
        Delete: { Objects: objects, Quiet: true },
      })
    );
  }

  async getAssetFileURL(parentKey: string, fileName: string) {
    await this.checkCredentials();
    const s3 = this.requireS3();
    if (!parentKey.trim()) throw new Error("No key provided.");
    return getSignedUrl(
      s3,
      new GetObjectCommand({
        Bucket: this.bucket,
        Key: `${parentKey}/${ASSETS_FOLDER_NAME}/${fileName}`,
      }),
      { expiresIn: 3600 }
    );
  }

  async copyFolder(sourceFolderKey: string, targetFolderKey: string) {
    await this.checkCredentials();
    const s3 = this.requireS3();

    const sourceName = sourceFolderKey.replace(/\/$/, "").split("/").at(-1)!;

    const listed = await s3.send(
      new ListObjectsV2Command({ Bucket: this.bucket, Prefix: sourceFolderKey })
    );
    await Promise.all(
      (listed.Contents ?? [])
        .filter(item => item.Key && !item.Key.endsWith(EDITOR_FILE_NAME))
        .map(item => {
          // If teacher changed the folder name, make sure XHTML has the same name.
          const newKey = item.Key!.endsWith(".xhtml")
            ? `${targetFolderKey}${sourceName}/${sourceName}.xhtml`
            : item.Key!.replace(sourceFolderKey, `${targetFolderKey}${sourceName}`);
          return s3.send(
            new CopyObjectCommand({
              Bucket: this.bucket,
              CopySource: `${this.bucket}/${item.Key!}`,
              Key: newKey,
            })
          );
        })
    );
  }

  moveFolder(sourceFolderKey: string, targetFolderKey: string) {
    if (!sourceFolderKey.endsWith("/") || !targetFolderKey.endsWith("/")) {
      return Promise.reject(
        new Error(
          `Expected folder path ending with '/', got: ${sourceFolderKey} and ${targetFolderKey}`
        )
      );
    }
    return api.post<{ from_prefix: string; to_prefix: string }, void>(
      "/admin/library/move",
      {
        from_prefix: sourceFolderKey,
        to_prefix: targetFolderKey,
      }
    );
  }

  clearCredentials() {
    this.s3 = null;
  }
}

function extractFilename(url: string) {
  return url.substring(url.lastIndexOf("/") + 1, url.indexOf("?"));
}
